import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Package, Receipt, ShoppingCart, Book, Users, BarChart3, Settings,
  X, ChevronDown, ChevronRight, FileText, UserCircle, Truck, ClipboardList,
  PackageCheck, CreditCard, BookOpen, FileSpreadsheet, Wallet, Scale,
  TrendingUp, PieChart, FileBarChart, ShoppingBag, Boxes, DollarSign, Zap,
  ReceiptText, ArrowRightLeft, LineChart, Award, Coins, LogOut, Target
} from 'lucide-react';

const Sidebar = ({ isOpen, toggleSidebar, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [salesExpanded, setSalesExpanded] = useState(false);
  const [purchaseExpanded, setPurchaseExpanded] = useState(false);
  const [accountsExpanded, setAccountsExpanded] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(false);

  const salesSubMenu = [
    { name: 'Quick Sale', path: '/dashboard/sales/quick-sale', icon: Zap },
    { name: 'Customers', path: '/customers', icon: UserCircle },
    { name: 'Invoices', path: '/sales/invoices', icon: FileText },
    { name: 'Quotations', path: '/sales/quotations', icon: FileText },
    { name: 'Delivery Challans', path: '/sales/delivery-challans', icon: Truck },
  ];
  const purchaseSubMenu = [
    { name: 'Suppliers', path: '/purchase/suppliers', icon: Truck },
    { name: 'Purchase Orders', path: '/purchase/orders', icon: ClipboardList },
    { name: 'Receipts (GRN)', path: '/purchase/receipts', icon: PackageCheck },
    { name: 'Payments', path: '/purchase/payments', icon: CreditCard },
  ];
  const accountsSubMenu = [
    { name: 'Chart of Accounts', path: '/accounts/chart', icon: BookOpen },
    { name: 'Journal Entries', path: '/accounts/journal', icon: FileSpreadsheet },
    { name: 'Vouchers', path: '/accounts/vouchers', icon: Wallet },
    { name: 'Ledger', path: '/accounts/ledger', icon: Book },
    { name: 'Trial Balance', path: '/accounts/trial-balance', icon: Scale },
    { name: 'Profit & Loss', path: '/accounts/profit-loss', icon: TrendingUp },
    { name: 'Balance Sheet', path: '/accounts/balance-sheet', icon: PieChart },
    { name: 'Cash Flow', path: '/dashboard/accounts/cash-flow', icon: ArrowRightLeft },
    { name: 'Owner Transactions', path: '/dashboard/capital', icon: Coins },
  ];
  const reportsSubMenu = [
    { name: 'Sales Report', path: '/dashboard/reports/sales', icon: FileBarChart },
    { name: 'Purchase Report', path: '/dashboard/reports/purchase', icon: ShoppingBag },
    { name: 'Inventory Report', path: '/dashboard/reports/inventory', icon: Boxes },
    { name: 'Profit Summary', path: '/dashboard/reports/profit', icon: DollarSign },
    { name: 'Cash Forecast', path: '/dashboard/reports/cash-forecast', icon: LineChart },
    { name: 'Profitability', path: '/dashboard/reports/profitability', icon: Award },
    { name: 'Customer Aging', path: '/dashboard/reports/aging', icon: Receipt },
    { name: 'Regression Report', path: '/dashboard/reports/regression', icon: TrendingUp },
  ];

  const isActivePath = (path) => location.pathname === path;
  const isDashActive = isActivePath('/dashboard');
  const isInventoryActive = isActivePath('/inventory');
  const isSalesActive = location.pathname.startsWith('/sales') || location.pathname === '/customers' || location.pathname.startsWith('/dashboard/sales');
  const isPurchaseActive = location.pathname.startsWith('/purchase');
  const isExpensesActive = location.pathname.startsWith('/dashboard/expenses');
  const isAccountsActive = location.pathname.startsWith('/accounts') || location.pathname.startsWith('/dashboard/accounts') || location.pathname.startsWith('/dashboard/capital');
  const isHrActive = location.pathname.startsWith('/hr');
  const isBudgetActive = location.pathname.startsWith('/budget');
  const isReportsActive = location.pathname.startsWith('/dashboard/reports');
  const isSettingsActive = location.pathname.startsWith('/dashboard/settings') || location.pathname.startsWith('/settings');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const navItemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 10,
    color: active ? '#0F172A' : '#64748B',
    fontSize: 13.5,
    fontWeight: active ? 500 : 400,
    cursor: 'pointer',
    transition: 'background .15s, color .15s',
    position: 'relative',
    textDecoration: 'none',
    background: active ? '#F1F5F9' : 'transparent',
    border: 'none',
    textAlign: 'left',
    width: '100%',
  });

  return (
    <>
      <aside
        className={`fixed left-0 top-0 h-full transition-all duration-300 ease-in-out z-40 flex flex-col ${
          isOpen ? 'w-64' : 'w-0 overflow-hidden'
        }`}
        style={{
          padding: isOpen ? '20px 0' : 0,
          background: 'linear-gradient(180deg, #e8ecf1 0%, #dce3ed 100%)',
          borderRight: '1px solid #c8d0dc',
        }}
        data-testid="sidebar"
      >
        {isOpen && (
          <>
            {/* Brand */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px 20px' }}>
              <img src="/logo.png" alt="SAMS" style={{ width: 140, height: 'auto', objectFit: 'contain' }} data-testid="logo" />
              <button
                onClick={toggleSidebar}
                className="md:hidden"
                data-testid="sidebar-toggle-mobile"
                aria-label="Close sidebar"
                style={{ color: '#64748B', padding: 4, borderRadius: 8, background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>

            <nav style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '0 10px' }} data-testid="sidebar-nav">
              <Link to="/dashboard" style={navItemStyle(isDashActive)} data-testid="nav-dashboard">
                <Home strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span>Dashboard</span>
              </Link>

              <Link to="/inventory" style={navItemStyle(isInventoryActive)} data-testid="nav-inventory">
                <Package strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span>Inventory</span>
              </Link>

              <button
                type="button"
                onClick={() => setSalesExpanded((v) => !v)}
                style={navItemStyle(isSalesActive)}
                data-testid="nav-sales"
              >
                <Receipt strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span style={{ flex: 1 }}>Sales</span>
                {salesExpanded ? <ChevronDown style={{ width: 14, height: 14, opacity: 0.5 }} /> : <ChevronRight style={{ width: 14, height: 14, opacity: 0.5 }} />}
              </button>
              {salesExpanded && (
                <div style={{ margin: '2px 0 4px 22px', paddingLeft: 10, borderLeft: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {salesSubMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 12px', borderRadius: 8, fontSize: 12.5,
                        color: isActivePath(item.path) ? '#1a1a2e' : '#6b7280',
                        textDecoration: 'none',
                        fontWeight: isActivePath(item.path) ? 500 : 400,
                        background: isActivePath(item.path) ? 'linear-gradient(135deg, #d1d5db 0%, #c8cdd5 100%)' : 'transparent',
                      }}
                      data-testid={`nav-${item.name.toLowerCase()}`}
                    >
                      <item.icon strokeWidth={1.5} style={{ width: 14, height: 14, flexShrink: 0 }} />
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setPurchaseExpanded((v) => !v)}
                style={navItemStyle(isPurchaseActive)}
                data-testid="nav-purchase"
              >
                <ShoppingCart strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span style={{ flex: 1 }}>Purchase</span>
                {purchaseExpanded ? <ChevronDown style={{ width: 14, height: 14, opacity: 0.5 }} /> : <ChevronRight style={{ width: 14, height: 14, opacity: 0.5 }} />}
              </button>
              {purchaseExpanded && (
                <div style={{ margin: '2px 0 4px 22px', paddingLeft: 10, borderLeft: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {purchaseSubMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 12px', borderRadius: 8, fontSize: 12.5,
                        color: isActivePath(item.path) ? '#1a1a2e' : '#6b7280',
                        textDecoration: 'none',
                        fontWeight: isActivePath(item.path) ? 500 : 400,
                        background: isActivePath(item.path) ? 'linear-gradient(135deg, #d1d5db 0%, #c8cdd5 100%)' : 'transparent',
                      }}
                      data-testid={`nav-${item.name.toLowerCase().replace(/[() ]/g, '-')}`}
                    >
                      <item.icon strokeWidth={1.5} style={{ width: 14, height: 14, flexShrink: 0 }} />
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              )}

              <Link to="/dashboard/expenses" style={navItemStyle(isExpensesActive)} data-testid="nav-expenses">
                <ReceiptText strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span>Expenses</span>
              </Link>

              <button
                type="button"
                onClick={() => setAccountsExpanded((v) => !v)}
                style={navItemStyle(isAccountsActive)}
                data-testid="nav-accounts"
              >
                <Book strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span style={{ flex: 1 }}>Accounts</span>
                {accountsExpanded ? <ChevronDown style={{ width: 14, height: 14, opacity: 0.5 }} /> : <ChevronRight style={{ width: 14, height: 14, opacity: 0.5 }} />}
              </button>
              {accountsExpanded && (
                <div style={{ margin: '2px 0 4px 22px', paddingLeft: 10, borderLeft: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {accountsSubMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 12px', borderRadius: 8, fontSize: 12.5,
                        color: isActivePath(item.path) ? '#1a1a2e' : '#6b7280',
                        textDecoration: 'none',
                        fontWeight: isActivePath(item.path) ? 500 : 400,
                        background: isActivePath(item.path) ? 'linear-gradient(135deg, #d1d5db 0%, #c8cdd5 100%)' : 'transparent',
                      }}
                      data-testid={`nav-${item.name.toLowerCase().replace(/[& ]/g, '-')}`}
                    >
                      <item.icon strokeWidth={1.5} style={{ width: 14, height: 14, flexShrink: 0 }} />
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              )}

              <div style={{ fontSize: 10.5, color: '#94A3B8', letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 600, padding: '16px 12px 8px' }}>Workspace</div>

              <Link to="/budget" style={navItemStyle(isBudgetActive)} data-testid="nav-budget">
                <Target strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span>Budget</span>
              </Link>

              <Link to="/hr" style={navItemStyle(isHrActive)} data-testid="nav-hr">
                <Users strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span>HR</span>
              </Link>

              <button
                type="button"
                onClick={() => setReportsExpanded((v) => !v)}
                style={navItemStyle(isReportsActive)}
                data-testid="nav-reports"
              >
                <BarChart3 strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span style={{ flex: 1 }}>Reports</span>
                {reportsExpanded ? <ChevronDown style={{ width: 14, height: 14, opacity: 0.5 }} /> : <ChevronRight style={{ width: 14, height: 14, opacity: 0.5 }} />}
              </button>
              {reportsExpanded && (
                <div style={{ margin: '2px 0 4px 22px', paddingLeft: 10, borderLeft: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {reportsSubMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 12px', borderRadius: 8, fontSize: 12.5,
                        color: isActivePath(item.path) ? '#1a1a2e' : '#6b7280',
                        textDecoration: 'none',
                        fontWeight: isActivePath(item.path) ? 500 : 400,
                        background: isActivePath(item.path) ? 'linear-gradient(135deg, #d1d5db 0%, #c8cdd5 100%)' : 'transparent',
                      }}
                      data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                    >
                      <item.icon strokeWidth={1.5} style={{ width: 14, height: 14, flexShrink: 0 }} />
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              )}

              <Link
                to="/dashboard/settings/opening-balances"
                style={navItemStyle(isSettingsActive)}
                data-testid="nav-settings"
              >
                <Settings strokeWidth={1.5} style={{ width: 18, height: 18, flexShrink: 0 }} /><span>Settings</span>
              </Link>
            </nav>

            <div style={{ marginTop: 'auto', padding: '10px 15px', borderTop: '1px solid rgba(255,255,255,0.3)' }}>
              <button
                onClick={handleLogout}
                title="Log out"
                data-testid="logout-button"
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '8px 12px', borderRadius: 10,
                  color: '#64748B', background: 'transparent', border: 'none',
                  cursor: 'pointer', fontSize: 13.5, fontWeight: 400,
                  transition: 'background .15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.6)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <LogOut style={{ width: 18, height: 18 }} strokeWidth={1.5} />
                <span>Log out</span>
              </button>
            </div>
          </>
        )}
      </aside>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={toggleSidebar}
          data-testid="sidebar-overlay"
        />
      )}
    </>
  );
};

export default Sidebar;
